import { Controller } from "@hotwired/stimulus";
import { trapFocus, releaseFocusTrap } from "../utils/FocusTrap";

export default class extends Controller {
  static targets = [
    "dropdownImage",
    "createListModal",
    "createListModalError",
    "resetTodoModal",
    "removeFromListTodoModal",
    "removeAssignedTodoModal",
    "removeTodoModal",
    "assignedCourseName"
  ]

  connect() {
    this.handleKeydown = this.handleKeydown.bind(this);
    document.addEventListener('keydown', this.handleKeydown);
  }

  disconnect() {
    document.removeEventListener('keydown', this.handleKeydown);
    if (this.hasRemoveAssignedTodoModalTarget) {
      releaseFocusTrap(this.removeAssignedTodoModalTarget, { restoreFocus: false });
    }
  }

  handleKeydown(event) {
    if (this.hasResetTodoModalTarget && !this.resetTodoModalTarget.classList.contains('hidden')) {
      this.handleModalKeydown(event, this.resetTodoModalTarget, 'hideResetTodoModal');
    }
    if (this.hasRemoveFromListTodoModalTarget && !this.removeFromListTodoModalTarget.classList.contains('hidden')) {
      this.handleModalKeydown(event, this.removeFromListTodoModalTarget, 'hideRemoveFromListTodoModal');
    }
    if (this.hasRemoveAssignedTodoModalTarget && !this.removeAssignedTodoModalTarget.classList.contains('hidden')) {
      this.handleModalKeydown(event, this.removeAssignedTodoModalTarget, 'hideRemoveAssignedTodoModal');
    }
    if (this.hasRemoveTodoModalTarget && !this.removeTodoModalTarget.classList.contains('hidden')) {
      this.handleModalKeydown(event, this.removeTodoModalTarget, 'hideRemoveTodoModal');
    }
    if (this.hasCreateListModalTarget && !this.createListModalTarget.classList.contains('hidden')) {
      this.handleModalKeydown(event, this.createListModalTarget, 'hideCreateListModal');
    }
  }

  handleModalKeydown(event, modal, hideModalMethod) {
    if (event.key === 'Escape') {
      this[hideModalMethod](event);
      return;
    }

    if (event.key !== 'Enter') return;

    const active = document.activeElement;
    if (
      active instanceof HTMLElement &&
      modal.contains(active) &&
      active.matches('button, input[type="submit"]')
    ) {
      return;
    }

    modal.querySelector('input[type="submit"], button[type="submit"]')?.click();
  }

  toggleTodoListDropdown(){
    const todoListDropdownDiv = document.getElementById("todo-list-dropdown-div");
    todoListDropdownDiv.classList.toggle("show");
    this.dropdownImageTarget.classList.toggle("rotated");
  }

  openCreateListModal(e){
    e.preventDefault();
    e.stopImmediatePropagation();

    this.createListModalTarget.classList.remove("hidden");
    document.getElementById('title').focus();
  }

  submitCreateListModal(e){
    if (document.getElementById("title").value!==""){
      this.createListModalTarget.classList.add("hidden");
    }else{
      e.preventDefault();
      e.stopImmediatePropagation();
      this.createListModalErrorTarget.textContent = "Please enter a name of the list";
    }
  }

  hideCreateListModal(e){
    this.createListModalTarget.classList.add("hidden");
  }

  openResetTodoModal(e){
    e.preventDefault();
    e.stopImmediatePropagation();
    this.resetTodoModalTarget.classList.remove("hidden");
  }

  hideResetTodoModal(e){
    this.resetTodoModalTarget.classList.add("hidden");
  }

  updateMoreActionDetails(event) {
    const checkboxesArray = document.getElementsByName("todo_checkboxes");
    const checkedCheckboxes = Array.from(checkboxesArray).filter(checkbox => checkbox.checked);
    
    const todoIds = checkedCheckboxes.map(checkbox => checkbox.dataset.todoId).join(',');
    const documentTitles = checkedCheckboxes.map(checkbox => checkbox.dataset.documentTitle);

    this.selectedHasAssignedTodos = checkedCheckboxes.some(
      checkbox => checkbox.dataset.customCourseAssigned === 'true'
    );
    this.selectedAssignedCourseNames = [
      ...new Set(
        checkedCheckboxes
          .filter(checkbox => checkbox.dataset.customCourseAssigned === 'true')
          .map(checkbox => checkbox.dataset.customCourseName)
          .filter(Boolean)
      )
    ];
    
    if(document.getElementById('todo-items-name-reset-modal')){
      let todoItems= document.getElementById('todo-items-name-reset-modal');
      todoItems.innerHTML = '';
      documentTitles.forEach(title => {
        let li = document.createElement('li');
        li.textContent = title;
        li.style.listStyleType = 'disc';
        todoItems.appendChild(li);
      });
    }

    if(document.getElementById('todo_items_checkbox_values')){
      document.getElementById('todo_items_checkbox_values').value = todoIds;
    }

    if(document.getElementById('todo_items_move_to_another_list_values')){
      document.getElementById('todo_items_move_to_another_list_values').value = todoIds;
    }

    this.populateRemoveModalLists(todoIds, documentTitles);
  }

  populateRemoveModalLists(todoIds, documentTitles) {
    const listConfigs = [
      { idsField: 'todo_items_remove_checkbox_values', listId: 'todo-items-name-remove-from-list-modal' },
      { idsField: 'todo_items_remove_assigned_checkbox_values', listId: 'todo-items-name-remove-assigned-modal' },
      { idsField: 'todo_items_remove_modal_checkbox_values', listId: 'todo-items-name-remove-modal' }
    ];

    listConfigs.forEach(({ idsField, listId }) => {
      const idsInput = document.getElementById(idsField);
      if (idsInput) {
        idsInput.value = todoIds;
      }

      const listElement = document.getElementById(listId);
      if (!listElement) return;

      listElement.innerHTML = '';
      documentTitles.forEach(title => {
        let li = document.createElement('li');
        li.textContent = title;
        li.style.listStyleType = 'disc';
        listElement.appendChild(li);
      });
    });
  }

  openRemoveFromListTodoModal(e) {
    e.preventDefault();
    e.stopImmediatePropagation();

    if (this.hasRemoveFromListTodoModalTarget) {
      this.removeFromListTodoModalTarget.classList.remove("hidden");
    }
  }

  openRemoveTodoModal(e) {
    e.preventDefault();
    e.stopImmediatePropagation();

    if (this.selectedHasAssignedTodos) {
      this.openRemoveAssignedTodoModal(e);
      return;
    }

    if (this.hasRemoveTodoModalTarget) {
      this.removeTodoModalTarget.classList.remove("hidden");
    }
  }

  openRemoveAssignedTodoModal(e) {
    if (e) {
      e.preventDefault();
      e.stopImmediatePropagation();
    }

    if (this.hasAssignedCourseNameTarget) {
      const courseNames = this.selectedAssignedCourseNames || [];
      this.assignedCourseNameTarget.textContent = courseNames.join(', ');
    }

    if (this.hasRemoveAssignedTodoModalTarget) {
      const modal = this.removeAssignedTodoModalTarget;
      const previousFocusElement =
        e?.currentTarget instanceof HTMLElement
          ? e.currentTarget
          : (document.activeElement instanceof HTMLElement ? document.activeElement : null);

      modal.classList.remove("hidden");
      trapFocus(modal, {
        previousFocusElement,
        initialFocusSelector: "#remove_assigned_link",
      });
    }
  }

  hideRemoveFromListTodoModal(e){
    this.removeFromListTodoModalTarget.classList.add("hidden");
  }

  hideRemoveAssignedTodoModal(e) {
    const modal = this.removeAssignedTodoModalTarget;
    releaseFocusTrap(modal);
    modal.classList.add("hidden");
  }

  hideRemoveTodoModal(e) {
    this.removeTodoModalTarget.classList.add("hidden");
  }

  handleKeypressToggleTodoListDropdown(event) {
    if (event.keyCode === 13) {
      event.preventDefault();
      this.toggleTodoListDropdown();
    }
  }

  checkEnterFilterKey(event) {
    if (event.keyCode === 13) {
      event.preventDefault();
      const checkboxId = event.target.getAttribute('for');
      const checkbox = document.getElementById(checkboxId);
      checkbox.click();
    }
  }

  handleKeypressCheckbox(event) {
    if (event.keyCode === 13) {
      event.preventDefault();
      event.target.click(); 
    }
  }

  toggleTodoListFiltersAccordion(event) {
    let cookies = document.cookie.split('; ');
    let cookie = cookies.find(row => row.startsWith('todoListFiltersAccordion='));
  
    if(cookie === undefined || cookie.split('=')[1] === "false") {
      document.cookie = "todoListFiltersAccordion=true; path=/";
    }else{
      document.cookie = "todoListFiltersAccordion=false; path=/";
    }
  }
}
